import { createFileRoute, Link } from "@tanstack/react-router";
import { Factory, MapPin, Building2, Globe2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import {
  ESTATE_REGIONS,
  type EstateRegion,
  type IndustrialEstate,
} from "@/content/industrial-estates/estates";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

type EstatesIndexData = {
  grouped: Record<EstateRegion, IndustrialEstate[]>;
  estates: IndustrialEstate[];
};

export const Route = createFileRoute("/industrial-estates/")({
  component: IndustrialEstatesIndex,
  loader: async (): Promise<EstatesIndexData> => {
    const { INDUSTRIAL_ESTATES, estatesByRegion } = await import(
      "@/content/industrial-estates/estates"
    );
    return { grouped: estatesByRegion(), estates: INDUSTRIAL_ESTATES };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const estates = loaderData?.estates ?? [];
    const base = pageHeadForProvince({
      title: `Notary & Work Permit สำหรับนิคมอุตสาหกรรมทั่วไทย (B2B)`,
      description: `บริการ Notary, Work Permit, แปลรับรอง, MFA และ Police Clearance แบบ B2B สำหรับโรงงานใน ${estates.length || 30}+ นิคมอุตสาหกรรมหลักของไทย — EEC, Amata, WHA, Rojana, IEAT.`,
      path: "/industrial-estates",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/industrial-estates")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "นิคมอุตสาหกรรม", url: "/industrial-estates" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Industrial Estates Served",
            itemListElement: estates.map((e, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/industrial-estates/${e.slug}`,
              name: `${e.nameEn} (${e.province})`,
            })),
          }),
        },
      ],
    };
  },
});

function IndustrialEstatesIndex() {
  const { grouped, estates } = Route.useLoaderData() as EstatesIndexData;
  const regionOrder: EstateRegion[] = [
    "eastern-seaboard",
    "bangkok-metro",
    "ayutthaya-corridor",
    "central-north",
  ];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold">
              <Factory className="h-3.5 w-3.5" /> B2B Industrial Estates Coverage
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">
              Notary & Work-Permit สำหรับโรงงานในนิคมอุตสาหกรรมทั่วไทย
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              ทีมกฎหมายและฝ่าย HR ของบริษัท JV ในนิคม EEC, Amata, WHA, Rojana และ IEAT
              สามารถจ้างเราเป็น outsourced legal team เพื่อจัดการ Work Permit, BOI documentation,
              certified translation, MFA legalization และ embassy walk-ins แบบ batch — ลด downtime
              ของผู้บริหารต่างชาติและประหยัดเวลาเดินทางเข้ากรุงเทพ
            </p>
            <div className="mt-6 grid max-w-2xl grid-cols-2 gap-3 md:grid-cols-4">
              <Stat icon={Building2} label="นิคมที่ครอบคลุม" value={`${estates.length}+`} />
              <Stat
                icon={Factory}
                label="โรงงานในเครือข่าย"
                value={`${estates.reduce((a, e) => a + e.factories, 0).toLocaleString()}+`}
              />
              <Stat icon={MapPin} label="จังหวัด" value={`${new Set(estates.map((e) => e.province)).size}`} />
              <Stat icon={Globe2} label="ภาษาเอกสาร" value="JP/CN/EN/KR/EU" />
            </div>
          </div>
        </section>

        {regionOrder.map((r) => (
          <section key={r} className="border-b border-border">
            <div className="mx-auto max-w-6xl px-4 py-10">
              <h2 className="text-2xl font-bold">
                <span className="text-gold">{ESTATE_REGIONS[r].th}</span>{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  · {ESTATE_REGIONS[r].en}
                </span>
              </h2>
              <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {grouped[r].map((e) => (
                  <Link
                    key={e.slug}
                    to="/industrial-estates/$estate"
                    params={{ estate: e.slug }}
                    className="group rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-lg"
                  >
                    <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      {e.province} · {e.operator}
                    </div>
                    <div className="mt-1 text-base font-extrabold text-foreground">
                      {e.nameEn}
                    </div>
                    <div className="text-sm text-muted-foreground">{e.nameTh}</div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      {e.factories}+ factories · นักลงทุนหลัก{" "}
                      <span className="font-semibold text-foreground">
                        {e.dominantInvestors.join("/")}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ))}
      </main>
      <SiteFooter />
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Factory; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
      <div className="rounded-lg bg-primary/10 p-2 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-sm font-bold">{value}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}